import SendProgress from "../SendProgress"
import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"
import { ModalLoadingOverlay, useDetectedModalLoading } from "@/components/ui/modal-loading-overlay"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-[130] bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef(({ className, children, hideClose = false, loading = false, loadingStandalone = false, loadingVariant = "loading", loadingDetail = "", sending = false, sendingLabel = "Nachricht", sendingDetail = "", loadingLabel = "Bitte warten …", autoLoading = true, onEscapeKeyDown, onPointerDownOutside, ...props }, forwardedRef) => {
  const [contentNode, setContentNode] = React.useState(null)
  const detectedLoading = useDetectedModalLoading(contentNode, autoLoading && !sending)
  const isLoading = !sending && Boolean(loading || detectedLoading)
  const setRefs = React.useCallback((node) => {
    if (node) setContentNode((current) => current === node ? current : node)
    if (typeof forwardedRef === "function") forwardedRef(node)
    else if (forwardedRef) forwardedRef.current = node
  }, [forwardedRef])

  return <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={setRefs}
      aria-busy={isLoading || sending || undefined}
      className={cn(
        "!fixed !left-1/2 !top-1/2 z-[131] grid box-border min-w-0 w-[calc(100vw-2rem)] max-w-lg !-translate-x-1/2 !-translate-y-1/2 gap-4 border bg-background p-4 sm:p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 rounded-2xl sm:rounded-xl max-h-[calc(100dvh-2rem)] overflow-x-hidden overflow-y-auto [&>*]:min-w-0 [&>*]:max-w-full",
        isLoading && loadingStandalone && "min-h-64",
        className
      )}
      {...props}
      onEscapeKeyDown={event => { if (sending || isLoading) event.preventDefault(); onEscapeKeyDown?.(event); }}
      onPointerDownOutside={event => { if (sending || isLoading) event.preventDefault(); onPointerDownOutside?.(event); }}>
      {sending ? <><div hidden>{children}</div><SendProgress label={sendingLabel} detail={sendingDetail} /></> : isLoading && loadingStandalone ? <div hidden>{children}</div> : children}
      <ModalLoadingOverlay visible={isLoading} label={loadingLabel} standalone={loadingStandalone} variant={loadingVariant} detail={loadingDetail} />
      {/* hideClose: fuer Dialoge, die einen laufenden Vorgang zeigen und
          waehrenddessen nicht weggeklickt werden sollen. */}
      {!hideClose && !isLoading && !sending && <DialogPrimitive.Close
        className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>}
    </DialogPrimitive.Content>
  </DialogPortal>
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
